/**
 * Sticky add-to-cart bar — a real submit button for the product form.
 *
 * The button carries `form="product-form-<section-id>"`, so pressing it submits
 * the actual product form and `product-form.js` handles it exactly as if the
 * in-page button had been pressed: same variant, quantity and cart drawer. This
 * element only does what the browser can't — slide the bar in once the real
 * button scrolls out of view (via the `--visible` class in `custom.css`), and
 * mirror that button's state so the two never disagree.
 *
 * A custom element rather than an inline script because `pdp-purchase-options.js`
 * replaces the section's innerHTML on a bundle swap: inline scripts wouldn't
 * re-run, but a custom element upgrades itself when it is inserted.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.sticky-add-to-cart__button');
        this.spinner = this.querySelector('.loading__spinner');

        const productInfo = this.closest('product-info');
        const productForm = (productInfo || document).querySelector('product-form');
        this.targetButton = productForm?.querySelector('[type="submit"]');

        // Without a button to mirror there is nothing to show; the bar stays in
        // its default hidden state (no --visible class).
        if (!this.button || !this.targetButton) return;

        // Only if the markup's form id didn't resolve to a form (quick-add modals,
        // section swaps) do we re-point the button. `getAttribute('id')`, not
        // `.id`: a form's named controls shadow its own properties, and this form
        // has an `<input name="id">`, so `form.id` is that input, not a string.
        if (!this.button.form) {
          const formId = productForm.querySelector('form')?.getAttribute('id');
          if (formId) this.button.setAttribute('form', formId);
        }

        this.updateVisibility = this.updateVisibility.bind(this);
        this.syncFromTarget = this.syncFromTarget.bind(this);

        this.visibilityObserver = new IntersectionObserver(this.updateVisibility, { threshold: 1 });
        this.visibilityObserver.observe(this.targetButton);
        window.addEventListener('resize', this.updateVisibility);

        // Quick-add modals sit above the page; hide the bar while one is open.
        this.modalObserver = new MutationObserver(this.updateVisibility);
        this.modalObserver.observe(document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['open'],
        });

        // The real button is what product-form.js disables, spins and relabels.
        this.stateObserver = new MutationObserver(this.syncFromTarget);
        this.stateObserver.observe(this.targetButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.syncFromTarget();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.modalObserver?.disconnect();
        this.stateObserver?.disconnect();
        window.removeEventListener('resize', this.updateVisibility);
      }

      updateVisibility() {
        const quickAddOpen = document.querySelector('quick-add-modal[open]');
        const rect = this.targetButton.getBoundingClientRect();
        const outOfView = rect.top < 0 || rect.bottom > window.innerHeight;

        this.classList.toggle('sticky-add-to-cart--visible', !quickAddOpen && outOfView);
      }

      syncFromTarget() {
        const loading = this.targetButton.classList.contains('loading');

        this.button.disabled = this.targetButton.disabled;
        if (this.targetButton.getAttribute('aria-disabled') === 'true') {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
        this.button.classList.toggle('loading', loading);
        this.spinner?.classList.toggle('hidden', !loading);

        const label = this.targetButton.querySelector('span');
        const stickyLabel = this.button.querySelector('span');
        if (label && stickyLabel) stickyLabel.textContent = label.textContent;
      }
    }
  );
}
